import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useHistory } from 'react-router-dom';
import { FaRegTimesCircle } from '@react-icons/all-files/fa/FaRegTimesCircle';
import axios from 'axios';

import { getPermissions, Permissions } from '../../../../../lib/permissions';
import { getLocationFromAddress } from '../../../../../lib/utils/map';
import { useSpots } from '../../../../../lib/spots';
import { UserPage } from '../../../../../components/UserPage';
import { Section } from '../../../../../components/Section';
import { Flash } from '../../../../../components/Flash';


export const EditSpot: FC = () => {
    const [spot, setSpot] = useState<Spot>();
    const { register: ref, handleSubmit, errors, setValue, clearErrors, setError } = useForm({
        criteriaMode: "all",
    });
    const [flash, setFlash] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [selectedImage, setSelectedImage] = useState<{ body: string; mimeType: string } | null>(null);
    const { get, edit, erase } = useSpots();
    const [isLocationEditorOpen, setIsLocationEditorOpen] = useState(false);
    const [coordinate, setCoordinate] = useState<Coordinate>();
    const history = useHistory();
    const params = useParams() as any;
    const [userPermissions, setUserPermissions] = useState<Permissions | null>(null);

    useEffect(() => {
        async function initialize() {
            const permissions = await getPermissions();
            if (!permissions.edit) {
                let redirectUrl = '/payments/pay/nishino';
                if (permissions.status === 'pause') {
                    redirectUrl = '/payments/suspend/1hc1srjr-johu-o7eg-33xn-r8u88p2slizf';
                } else if (permissions.status === 'unpaid') {
                    redirectUrl = '/mypages#payments';
                };
                window.location.href = redirectUrl;
                return;
            };
            try {
                const { data } = await axios.get('/api/profile_status');
                if (data.public) {
                    setUserPermissions(permissions);
                    setIsInitialized(true);
                } else {
                    window.location.href = '/mypages/edit';
                };
            } catch {
                window.location.href = '/';
            };
            const { id } = params;
            // @ts-ignore
            const spot = await get(id);
            if (!spot) {
                history.push('/map');
                return;
            };
            if (!spot.editable) {
                // @ts-ignore;
                history.push(`/map/spot/${id}`);
            } else {
                setSpot(spot);
            };
        };
        initialize();
    }, []);

    useEffect(() => {
        if (coordinate) {
            setValue('lat', coordinate.lat);
            setValue('lng', coordinate.lng);
        };
    }, [coordinate]);

    async function onSubmit(data: any) {
        if (loading) return false;
        setLoading(true);

        if (!spot) {
            return;
        }

        const fullAddress = data.pref + data.city + data.address;
        const oldAddress = spot.pref + spot.city + spot.address;
        let isManual = spot.manualInput;
        let location = {
            lat: spot.lat,
            lng: spot.lng,
        };
        if (!isManual && fullAddress !== oldAddress && !(data.lat && data.lng) && !(data.lat === spot.lat && data.lng === spot.lng)) {
            try {
                location = await getLocationFromAddress(fullAddress);
                clearErrors('pref');
                clearErrors('city');
                clearErrors('address');
            } catch {
                setError('pref', {
                    message: '入力された住所から座標を特定できませんでした。',
                    shouldFocus: true,
                });
                setError('city', {
                    message: '入力内容をご確認ください。',
                });
                setError('address', {
                    message: '入力内容をご確認ください。',
                })
                setLoading(false);
                return false;
            };
        } else if ((data.lat && data.lng) && !(data.lat === spot.lat && data.lng === spot.lng)) {
            location = {
                lat: Number(data.lat),
                lng: Number(data.lng),
            };
            isManual = true;
        };
        let mimeType;
        let body;
        if (data.cover_image.length > 0) {
            const reader = new FileReader();
            mimeType = data.cover_image[0].type;
            reader.readAsDataURL(data.cover_image[0]);
            body = await new Promise<string>((resolve) => {
                reader.onload = (loaded) => {
                    const body = loaded.target?.result ?? null;
                    // @ts-ignore
                    resolve(body);
                };
            });
        };
        let params;
        if (!body) {
            params = {
                id: spot.id,
                ...data,
                ...location,
                manual_input: isManual,
            };
        } else {
            params = {
                id: spot.id,
                ...data,
                ...location,
                cover_image: {
                    mimeType,
                    body: body.split(',')[1],
                },
                manual_input: isManual,
            };
        }

        const { error, errors } = await edit(params);

        if (errors) {
            for (const [name, msgs] of Object.entries<{ string: string }>(errors)) {
                for (const [_, msg] of Object.entries<string>(msgs)) {
                    setError(name, { type: 'manual', message: msg });
                }
            }
            setLoading(false);
            return false;
        }

        if (!error) {
            setLoading(false);
            history.push(
                `/map/spot/${spot.id}`,
                { flash: '店舗情報を編集しました' },
            );
        }

        setFlash("編集に失敗しました。");
        setLoading(false);
        return false;
    };

    function Cancel() {
        history.push(`/map/spot/${spot?.id}`);
    };

    async function Delete() {
        if (loading) return false;
        setLoading(!loading);
        const confirm = window.confirm('このお店を削除しますか？');
        if (!confirm) {
            setLoading(false);
            return false;
        }
        const { error } = await erase(spot!.id);
        if (!error) {
            setLoading(!loading);
            history.push('/map');
        } else {
            setFlash('店舗の削除に失敗しました。');
            setLoading(!loading);
        };
    };

    return (
        <UserPage>
            {flash ? <Flash color="#a94442" backgroundColor="#f2dede" flash={flash} /> : null}
            {(userPermissions && userPermissions.edit && isInitialized) && (
                <Section>
                    <div className="title">
                        <h1>お店の編集</h1>
                    </div>
                    {spot ? <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="wrapper">
                            <div className="label">ジャンル<span>（必須）</span></div>
                            <select ref={ref({ required: true })} name="genre" defaultValue={spot.genre}>
                                <option value="飲食店">飲食店</option>
                                <option value="ヘアサロン">ヘアサロン</option>
                                <option value="スナックキャンディ">スナックキャンディ</option>
                                <option value="宿泊">宿泊</option>
                                <option value="自販機">自販機</option>
                                <option value="保育園・幼稚園">保育園・幼稚園</option>
                                <option value="その他">その他</option>
                                <option value="居酒屋・バー">飲食店＞居酒屋・バー</option>
                                <option value="レストラン・カフェ">飲食店＞レストラン・カフェ</option>
                                <option value="スイーツ">飲食店＞スイーツ</option>
                                <option value="健康">その他＞健康</option>
                                <option value="ビューティ">その他＞ビューティ</option>
                                <option value="医療・福祉">その他＞医療・福祉</option>
                                <option value="小売">その他＞小売</option>
                                <option value="ビジネス">その他＞ビジネス</option>
                                <option value="レンタルスペース">その他＞レンタルスペース</option>
                                <option value="エンタメ・レジャー">その他＞エンタメ・レジャー</option>
                                <option value="習いごと">その他＞習いごと</option>
                                <option value="農業">その他＞農業</option>
                            </select>
                        </div>
                        <div className="wrapper">
                            <div className="label">お店の名前<span>（必須）</span></div>
                            <input type="text" ref={ref({ required: true })} name="name" defaultValue={spot.name} />
                            {errors.name && <span className="error-message">入力内容をご確認ください。</span>}
                        </div>
                        <div className="wrapper">
                            <div className="label">住所（都道府県）<span>（必須）</span></div>
                            <input type="text" ref={ref({ required: true })} name="pref" defaultValue={spot.pref} />
                            {errors.pref && <span className="error-message">{errors.pref.message}</span>}
                        </div>
                        <div className="wrapper">
                            <div className="label">住所（市区町村）<span>（必須）</span></div>
                            <input type="text" ref={ref({ required: true })} name="city" defaultValue={spot.city} />
                            {errors.city && <span className="error-message">{errors.city.message}</span>}
                        </div>
                        <div className="wrapper">
                            <div className="label">住所（それ以降）<span>（必須）</span></div>
                            <input type="text" ref={ref({ required: true })} name="address" defaultValue={spot.address} />
                            {errors.address && <span className="error-message">{errors.address.message}</span>}
                        </div>
                        <div className="wrapper">
                            <div className="label">お店についての説明<span>（必須）</span></div>
                            <textarea cols={30} rows={8} ref={ref({ required: true })} name="description" defaultValue={spot.description}></textarea>
                            {!errors.description && <div className="limit">255文字以内</div>}
                            <span className="error-message">
                                {errors.description?.type === 'required' && '入力内容をご確認ください。'}
                                {errors.description?.type === 'manual' && errors.description.message}
                            </span>
                        </div>
                        <div className="wrapper">
                            <div className="label">営業時間<span>（必須）</span></div>
                            <textarea cols={30} rows={5} ref={ref({ required: true })} name="opening_hours" defaultValue={spot.openingHours}></textarea>
                            {!errors.opening_hours && <div className="limit">255文字以内</div>}
                            <span className="error-message">
                                {errors.opening_hours?.type === 'required' && '入力内容をご確認ください。'}
                                {errors.opening_hours?.type === 'manual' && errors.opening_hours.message}
                            </span>
                        </div>
                        <div className="wrapper">
                            <div className="label">メンバー特典</div>
                            <p>※特典を用意する場合は、サロンメンバー用の連絡方法も明記してください。</p>
                            <textarea cols={30} rows={5} ref={ref} name="member_benefits" defaultValue={spot.memberBenefits}></textarea>
                            {!errors.member_benefits && <div className="limit">255文字以内</div>}
                            <span className="error-message">
                                {errors.member_benefits?.type === 'required' && '入力内容をご確認ください。'}
                                {errors.member_benefits?.type === 'manual' && errors.member_benefits.message}
                            </span>
                        </div>
                        <div className="wrapper">
                            <div className="label">カバー画像</div>
                            <p>※画像サイズは150×660推奨</p>
                            <div className="cover-image-container">
                                <div className="cover-image-selector">
                                    {!selectedImage && (
                                        <img className="selected-image" src={spot.coverImage} />
                                    )}
                                    {selectedImage && (
                                        <img className="selected-image" src={selectedImage.body} />
                                    )}
                                    <div className="change-image-button">
                                        <label htmlFor="cover-image">
                                            <div>
                                                変更する
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <input
                                id="cover-image"
                                className="cover-image"
                                name="cover_image"
                                type="file"
                                ref={ref}
                                onChange={async (e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        let mimeType;
                                        let body;
                                        const reader = new FileReader();
                                        mimeType = e.target.files[0].type;
                                        reader.readAsDataURL(e.target.files[0]);
                                        body = await new Promise<string>((resolve) => {
                                            reader.onload = (loaded) => {
                                                const body = loaded.target?.result ?? null;
                                                // @ts-ignore
                                                resolve(body);
                                            };
                                        });
                                        setSelectedImage({
                                            mimeType,
                                            body,
                                        });
                                    } else {
                                        setSelectedImage(null);
                                    }
                                }}
                            />
                        </div>
                        <div className="spot-options">
                            <div className="label">オプション（飲食店カテゴリーのみ）</div>
                            <div className="option">
                                <input type="checkbox" id="c1" ref={ref} name="delivery" defaultChecked={spot.delivery} />
                                <label htmlFor="c1" className="checkbox">デリバリーが可能</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c2" ref={ref} name="takeout" defaultChecked={spot.takeout} />
                                <label htmlFor="c2" className="checkbox">テイクアウトが可能</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c3" ref={ref} name="lunch" defaultChecked={spot.lunch} />
                                <label htmlFor="c3" className="checkbox">ランチ営業あり</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c4" ref={ref} name="dinner" defaultChecked={spot.dinner} />
                                <label htmlFor="c4" className="checkbox">ディナー営業あり</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c5" ref={ref} name="alcohol" defaultChecked={spot.alcohol} />
                                <label htmlFor="c5" className="checkbox">アルコールを飲める</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c6" ref={ref} name="cafe" defaultChecked={spot.cafe} />
                                <label htmlFor="c6" className="checkbox">カフェ利用が可能</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c8" ref={ref} name="chimney_coffee" defaultChecked={spot.chimneyCoffee} />
                                <label htmlFor="c8" className="checkbox">CHIMNEY COFFEE取扱</label>
                            </div>
                            <div className="option">
                                <input type="checkbox" id="c7" ref={ref} name="power_supply" defaultChecked={spot.powerSupply} />
                                <label htmlFor="c7" className="checkbox">電源利用が可能</label>
                            </div>
                        </div>
                        <div
                            className="location-editor-container"
                            onClick={() => setIsLocationEditorOpen(!isLocationEditorOpen)}
                        >
                            正しい位置にピンが表示されない方はこちら
                        </div>
                        {isLocationEditorOpen && (
                            <div className="location-editor">
                                <div className="label">緯度 / 経度</div>
                                <div className="notice">
                                    <span>※下のURLから緯度と経度の確認方法をご確認いただけます</span>
                                    <a target="_blank" href="https://support.google.com/maps/answer/18539">https://support.google.com/maps/answer/18539</a>
                                </div>
                                <div className="location-input-group">
                                    <input
                                        type="number"
                                        step="0.0000001"
                                        ref={ref}
                                        name="lat"
                                        defaultValue={spot.lat}
                                        onFocus={() => clearErrors(['pref', 'city', 'address'])}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const clipdata = e.clipboardData.getData('Text');
                                            if (clipdata.match(',')) {
                                                const [latstr, lngstr] = clipdata.split(',');
                                                const coord = {
                                                    lat: Math.ceil(Number(latstr) * 1000000) / 1000000,
                                                    lng: Math.ceil(Number(lngstr) * 10000000) / 10000000,
                                                };
                                                setCoordinate(coord);
                                            };
                                        }}
                                    />
                                    <span className="divider">/</span>
                                    <input
                                        type="number"
                                        step="0.0000001"
                                        ref={ref}
                                        name="lng"
                                        defaultValue={spot.lng}
                                        onFocus={() => clearErrors(['pref', 'city', 'address'])}
                                        onPaste={(e) => {
                                            const clipdata = e.clipboardData.getData('Text');
                                            if (clipdata.match(',')) {
                                                const [latstr, lngstr] = clipdata.split(',');
                                                const coord = {
                                                    lat: Math.ceil(Number(latstr) * 1000000) / 1000000,
                                                    lng: Math.ceil(Number(lngstr) * 10000000) / 10000000,
                                                };
                                                setCoordinate(coord);
                                            };
                                        }}
                                    />
                                </div>
                                {errors.lat || errors.lng && <span className="error-message">入力内容をご確認ください。</span>}
                            </div>
                        )}
                        <div className="action">
                            <div>
                                {spot.role === "管理者" && (
                                    <button className="delete-button" type="button" onClick={Delete}>
                                        削除する
                                    </button>
                                )}
                                <button className="submit-button" type="submit">
                                    決定する
                                </button>
                            </div>
                            <button className="cancel-button" type="button" onClick={Cancel}>
                                <FaRegTimesCircle style={{ marginRight: "2px" }} />
                                キャンセル
                            </button>
                        </div>
                    </form> : null}
                </Section>
            )}

            <style jsx>{`
                .title {
                    padding-top: 50px;
                    padding-bottom: 26px;
                }
                form {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }
                .wrapper {
                    margin-bottom: 20px;
                }
                h1 {
                    font-weight: normal;
                    font-size: 20px;
                    margin: 0;
                }
                .checkbox {
                    cursor: pointer;
                }
                .label {
                    border-left: solid 2px black;
                    padding-left: 8px;
                    height: 22px;
                    margin-bottom: 4px;
                    font-size: 14px;
                }
                .label > span {
                    color: red;
                }
                input[type=checkbox] {
                    cursor: pointer;
                }
                input,
                textarea {
                    border: solid 1px rgba(0,0,0,0.08);
                    resize: none;
                    margin: 0;
                    padding: 0;
                }
                textarea {
                    width: 100%;
                    padding-top: 4px;
                    padding-left: 14px;
                    padding-right: 14px;
                }
                .cover-image {
                    display: none;
                }
                .cover-image-container {
                    position: relative;
                    width: 100%;
                    padding-top: 40%;
                }
                .cover-image-selector {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 30px 26px 30px 26px;
                }
                .selected-image {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .upload-button-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }
                .upload-button-container > span {
                    font-size: 12px;
                }
                .upload-button {
                    width: 40px;
                    height: 40px;
                    margin-bottom: 8px;
                }
                .change-image-button {
                    position: absolute;
                    bottom: 5%;
                    right: 5%;
                }
                .change-image-button > label > div {
                    border: none;
                    background: #fff;
                    padding: 4px 8px 4px 8px;
                    border-radius: 4px;
                    cursor: pointer;
                }
                select,
                input[type=text],
                input[type=number] {
                    width: 100%;
                    height: 38px;
                    padding-left: 14px;
                    padding-right: 14px;
                }
                .error-message {
                    color: red;
                    font-size: 12px;
                }
                p {
                    font-size: 8px;
                    margin: 0;
                }
                .spot-options {
                    display: flex;
                    flex-direction: column;
                    margin-bottom: 20px;
                }
                .option {
                    font-size: 16px;
                }
                .option,
                .spot-options > .label {
                    margin-bottom: 20px;
                }
                input[type=checkbox] + label {
                    padding-left: 12px;
                }
                input[type=checkbox]:checked + label {
                    font-weight: bold;
                }
                .action {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                }
                .action > div {
                    margin-bottom: 50px;
                }
                .limit {
                    width: 100%;
                    text-align: right;
                    font-size: 4px;
                }
                .submit-button,
                .delete-button {
                    cursor: pointer;
                    width: 120px;
                    height: 40px;
                    border: none;
                    color: #fff;
                    border-radius: 24px;
                    outline: none;
                }
                .submit-button {
                    background-color: #fd6c07;
                }
                .delete-button {
                    background-color: gray;
                    margin-right: 20px;
                }
                .cancel-button {
                    cursor: pointer;
                    background: none;
                    border: none;
                    outline: none;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    color: black;
                }
                .location-editor-container {
                    cursor: pointer;
                    color: #ff6e01;
                    margin-bottom: ${isLocationEditorOpen ? "0px" : "32px"};
                    background: none;
                    border: none;
                    font-size: 14px;
                }
                .location-editor {
                    display: ${isLocationEditorOpen ? "block" : "none"};
                    margin-bottom: 32px;
                }
                .notice {
                    display: flex;
                    flex-direction: column;
                    margin-bottom: 8px;
                    margin-left: 20px;
                }
                .notice > span {
                    font-size: 12px;
                }
                .notice > a {
                    color: #ff6e01;
                    font-size: 12px;
                }
                .location-input-group {
                    display: flex;
                }
                .divider {
                    vertical-align: middle;
                    font-size: 20px;
                    padding-right: 10px;
                    padding-left: 10px;
                    color: #696969;
                }
                @media screen and (max-width: 540px) {
                    .title {
                        padding-top: 24px;
                        padding-bottom: 20px;
                    }
                    .label {
                        height: 18px;
                        font-size: 12px;
                    }
                    .wrapper {
                        margin-bottom: 12px;
                    }
                    .error-message {
                        font-size: 10px;
                    }
                    .option {
                        font-size: 14px;
                    }
                    .spot-options {
                        margin-bottom: 12px;
                    }
                    .option,
                    .spot-options > .label {
                        margin-bottom: 12px;
                    }
                    .location-editor {
                        font-size: 14px;
                    }
                    .action > div {
                        margin-bottom: 30px;
                    }
                    .location-editor-container {
                        font-size: 12px;
                    }
                    .notice {
                        margin-left: 0px;
                        justify-contens: center;
                        align-items: center;
                    }
                    .notice > span {
                        font-size: 10px;
                    }
                    .notice > a {
                        font-size: 10px;
                    }
                }
            `}</style>
        </UserPage>
    );
};
